import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal.tsx';
import { api } from '../lib/api.ts';
import { encodeSlideImage } from '../lib/encodeSlideImage.ts';
import { errorMessage } from '../lib/api.ts';
import { isFurniture } from '../lib/assetFurniture.ts';
import { ZoomIn, X } from '../lib/icons.tsx';
import AssetPreviewModal from './AssetPreviewModal.tsx';
import type { MediaPayload } from '../lib/useSlideFields.ts';
import type { BankItem } from '../../shared/deck.ts';
import type { BankPayload } from '../../shared/api.ts';

/** What a media slot takes, and what the bank shelf preselects for it. */
type MediaKind = 'image' | 'video';
type MediaHint = 'person' | 'logo' | null;

type MediaPickerModalProps = {
  kind?: MediaKind;
  /** Preselects the matching bank shelf and opens on it. */
  hint?: MediaHint;
  label?: string;
  onPick: (payload: MediaPayload) => void | Promise<void>;
  onCancel: () => void;
  busy?: boolean;
  /** The deck whose own assets/ shelf to offer. */
  slug?: string | null;
};

/** One file already in the deck's assets/. */
type DeckAsset = { name: string; size: number; mtime: number };

const VIDEO_EXT = /\.(mp4|webm)$/i;
const IMAGE_EXT = /\.(png|jpe?g|webp|gif|svg)$/i;
const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

/**
 * The Edit tab's media picker: fill or replace one media field on a slide.
 * Same picking-room shell as the add-slide chooser (modal--add-slide). Two
 * tabs — Upload (drag-drop / choose file; images get the client-side WebP
 * re-encode) and RKH bank (big tiles, category chips, cross-category
 * search). Resolves via onPick({ file, filename } | { bankId }); the caller
 * owns the POST.
 */
export default function MediaPickerModal({
  kind = 'image',
  hint = null,
  label,
  onPick,
  onCancel,
  busy,
  slug = null,
}: MediaPickerModalProps) {
  // People and logos usually live in the bank already — open there for them.
  const [tab, setTab] = useState(hint ? 'bank' : 'upload');
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      // Esc clears an in-flight search before it closes the modal.
      if (searchRef.current && document.activeElement === searchRef.current && searchRef.current.value) {
        setQuery('');
        return;
      }
      onCancel();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  useEffect(() => {
    if (tab === 'bank') searchRef.current?.focus();
  }, [tab]);

  async function handleFile(file: File | null | undefined) {
    if (!file || processing || busy) return;
    setError(null);
    try {
      if (kind === 'video') {
        if (!VIDEO_EXT.test(file.name)) throw new Error('This slot takes a video: export as MP4 or WebM.');
        if (file.size > MAX_VIDEO_BYTES) throw new Error('Video is over 100MB: compress it first.');
        await onPick({ file, filename: file.name });
      } else {
        if (!IMAGE_EXT.test(file.name)) throw new Error('This slot takes an image: PNG, JPG or WebP.');
        setProcessing(true);
        let payload: MediaPayload;
        if (/\.(gif|svg)$/i.test(file.name)) {
          payload = { file, filename: file.name }; // animated/vector — keep as-is
        } else {
          const encoded = await encodeSlideImage(file);
          payload = { file: encoded.blob, filename: encoded.filename };
        }
        setProcessing(false);
        await onPick(payload);
      }
    } catch (err) {
      setProcessing(false);
      setError(errorMessage(err));
    }
  }

  return (
    <Modal
      ariaLabel="Choose an image"
      onClose={onCancel}
      className="modal--add-slide media-picker"
      // This dialog keeps Escape: it clears an in-flight search BEFORE it
      // closes, and Modal cannot know about the search field.
      escape="none"
    >
      <header className="modal__header modal__header--add-slide">
        <h3 className="modal__title modal__title--sm" id="media-picker-title">
          {label || (kind === 'video' ? 'Add a video' : 'Add an image')}
        </h3>
        {/* The same segmented control the Edit tab uses — this modal opens
              from its Replace button, so a second look for one choice would
              read as a different app. */}
        <div className="seg" role="tablist" aria-label="Media source">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'upload'}
            className={'seg__btn' + (tab === 'upload' ? ' is-active' : '')}
            onClick={() => setTab('upload')}
          >
            Upload
          </button>
          {slug && (
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'deck'}
              className={'seg__btn' + (tab === 'deck' ? ' is-active' : '')}
              onClick={() => setTab('deck')}
            >
              Deck assets
            </button>
          )}
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'bank'}
            className={'seg__btn' + (tab === 'bank' ? ' is-active' : '')}
            onClick={() => setTab('bank')}
          >
            RKH bank
          </button>
        </div>
        <span className="add-slide__spacer" />
        {tab === 'bank' && (
          <input
            ref={searchRef}
            type="search"
            className="media-picker__search"
            placeholder="Search names, roles, labels…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search the RKH bank"
          />
        )}
        <button type="button" className="iconbtn" onClick={onCancel} title="Close" aria-label="Close">
          <X />
        </button>
      </header>

      {error && (
        <p className="media-picker__error" role="alert">
          {error}
        </p>
      )}

      {tab === 'upload' && (
        <div
          className="media-picker__upload"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            handleFile(e.dataTransfer.files?.[0]);
          }}
        >
          <button
            type="button"
            className="media-picker__dropzone"
            onClick={() => fileRef.current?.click()}
            disabled={processing || busy}
          >
            <span className="media-picker__dropzone-arrow" aria-hidden="true">
              ⬆
            </span>
            <span className="media-picker__dropzone-title">
              {busy
                ? 'Saving…'
                : processing
                  ? 'Preparing…'
                  : kind === 'video'
                    ? 'Drop an MP4 / WebM here'
                    : 'Drop a PNG / JPG here'}
            </span>
            <span className="muted small">or click to choose a file</span>
            <span className="muted small">
              {kind === 'video'
                ? 'MP4 or WebM, up to 100MB.'
                : 'PNG, JPG or WebP; large exports are optimised automatically.'}
            </span>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept={
              kind === 'video' ? 'video/mp4,video/webm' : 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml'
            }
            style={{ display: 'none' }}
            onChange={(e) => {
              handleFile(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>
      )}

      {tab === 'bank' && (
        <BankShelf
          kind={kind}
          hint={hint}
          query={query}
          busy={busy}
          onPick={async (item) => {
            setError(null);
            try {
              await onPick({ bankId: item.id });
            } catch (err) {
              setError(errorMessage(err));
            }
          }}
        />
      )}

      {tab === 'deck' && slug && (
        <DeckAssetsShelf
          kind={kind}
          slug={slug}
          busy={busy}
          onPick={async (name) => {
            setError(null);
            try {
              await onPick({ assetName: name });
            } catch (err) {
              setError(errorMessage(err));
            }
          }}
        />
      )}
    </Modal>
  );
}

/** This deck's own assets/ — everything already uploaded or fetched for this
 *  deck (including a photo a layout flip has orphaned), pickable without
 *  re-uploading. Same tile grid as the bank shelf. */
function DeckAssetsShelf({
  kind,
  slug,
  busy,
  onPick,
}: {
  kind: MediaKind;
  slug: string;
  busy?: boolean;
  onPick: (assetName: string) => void;
}) {
  const [state, setState] = useState<{ status: string; assets: DeckAsset[]; error: string | null }>({
    status: 'loading',
    assets: [],
    error: null,
  });

  // Index into state.assets — the viewer walks this shelf.
  const [magnified, setMagnified] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    // `usage=0`: this shelf fills one media field and never shows usage, so it
    // skips the server's per-slide scan.
    api.get<{ assets?: DeckAsset[] }>(`/api/assets/${slug}?usage=0`).then(
      (d) => {
        if (cancelled) return;
        const ext = kind === 'video' ? VIDEO_EXT : IMAGE_EXT;
        // Engine furniture (logos, the hummingbird, jungle artwork) is on
        // every deck — noise in a "your photos" shelf. Shared with the assets
        // picker so the two can't disagree about what counts.
        const assets = (d.assets || []).filter((a) => ext.test(a.name) && !isFurniture(a.name));
        setState({ status: 'ready', assets, error: null });
      },
      (err) => {
        if (!cancelled) setState({ status: 'error', assets: [], error: err.message });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [kind, slug]);

  if (state.status === 'loading') return <p className="muted small">Loading this deck’s assets…</p>;
  if (state.status === 'error') return <p className="media-picker__error">{state.error}</p>;
  if (state.assets.length === 0) {
    return (
      <p className="muted small">
        No {kind === 'video' ? 'videos' : 'images'} in this deck yet — upload one, or add files via the Assets drawer.
      </p>
    );
  }

  return (
    <div className="media-picker__shelf">
      <div className={'media-picker__grid' + (busy ? ' media-picker__grid--busy' : '')}>
        {state.assets.map((a, i) => (
          // The CELL is the grid item, not the tile: the magnifier has to be a
          // sibling of the tile button, and the tile is itself a button.
          <div key={a.name} className="media-picker__cell">
            <button
              type="button"
              className="media-picker__tile"
              onClick={() => onPick(a.name)}
              disabled={busy}
              title={a.name}
            >
              {kind === 'video' ? (
                <span className="media-picker__tile-frame media-picker__tile-frame--video" aria-hidden="true">
                  🎞
                </span>
              ) : (
                <img
                  className="media-picker__tile-frame"
                  src={`/preview/${slug}/assets/${encodeURIComponent(a.name)}`}
                  alt=""
                  loading="lazy"
                />
              )}
              <span className="media-picker__tile-label">{a.name}</span>
            </button>
            {/* Worth more here than anywhere else: one click on the tile
                replaces the slide's media outright, so looking first is the
                whole point. */}
            <button
              type="button"
              className="asset-magnify"
              title="Take a closer look"
              aria-label={`Preview ${a.name}`}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setMagnified(i);
              }}
            >
              <ZoomIn />
            </button>
          </div>
        ))}
      </div>

      {magnified != null && state.assets.length > 0 && (
        <AssetPreviewModal
          items={state.assets.map((a) => ({
            key: a.name,
            src: `/preview/${slug}/assets/${encodeURIComponent(a.name)}`,
            kind: kind === 'video' ? 'video' : 'image',
            label: a.name,
            size: a.size,
          }))}
          index={Math.min(magnified, state.assets.length - 1)}
          onClose={() => setMagnified(null)}
        />
      )}
    </div>
  );
}

/** The RKH asset-bank shelf: big tiles, category chips with counts, and
 *  cross-category search (a query searches the WHOLE bank — searching is a
 *  jump, not a filter within the selected shelf). */
function BankShelf({
  kind,
  hint,
  query,
  busy,
  onPick,
}: {
  kind: MediaKind;
  hint?: MediaHint;
  query: string;
  busy?: boolean;
  onPick: (item: BankItem) => void;
}) {
  const [state, setState] = useState<{ status: string; items: BankItem[]; error: string | null }>({
    status: 'loading',
    items: [],
    error: null,
  });
  // hint preselects the matching shelf (person → people, logo → logos).
  const [cat, setCat] = useState<string | null>(hint === 'person' ? 'people' : hint === 'logo' ? 'logos' : null);
  // Index into `shown`.
  const [magnified, setMagnified] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.get<Partial<BankPayload>>('/api/bank').then(
      (d) => {
        if (cancelled) return;
        const ext = kind === 'video' ? VIDEO_EXT : IMAGE_EXT;
        // Archived items are withdrawn from circulation — hidden here for the
        // same reason the server drops them from the AI catalogue.
        const items = (d.items || []).filter((it) => it.file && it.status !== 'archived' && ext.test(it.file));
        setState({ status: 'ready', items, error: null });
      },
      (err) => {
        if (!cancelled) setState({ status: 'error', items: [], error: err.message });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [kind]);

  const counts = useMemo(() => {
    const out = new Map();
    for (const it of state.items) out.set(it.category, (out.get(it.category) || 0) + 1);
    return out;
  }, [state.items]);

  const q = query.trim().toLowerCase();
  const shown = state.items.filter((it) => {
    if (q) {
      const hay = [it.label, it.id, it.meta?.name, it.meta?.role].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    }
    return !cat || it.category === cat;
  });

  if (state.status === 'loading') return <p className="muted small">Loading the bank…</p>;
  if (state.status === 'error') return <p className="media-picker__error">{state.error}</p>;
  if (state.items.length === 0) {
    return (
      <p className="muted small">
        No {kind === 'video' ? 'videos' : 'images'} in the RKH bank yet: manage it from the Library page.
      </p>
    );
  }

  return (
    <div className="media-picker__shelf">
      <div className="media-picker__chips">
        <button type="button" className={'chip' + (!cat && !q ? ' chip--active' : '')} onClick={() => setCat(null)}>
          All · {state.items.length}
        </button>
        {[...counts.keys()].map((c) => (
          <button
            key={c}
            type="button"
            className={'chip' + (cat === c && !q ? ' chip--active' : '')}
            onClick={() => setCat(c)}
          >
            {c} · {counts.get(c)}
          </button>
        ))}
        {q && <span className="muted small">searching the whole bank</span>}
      </div>
      <div className={'media-picker__grid' + (busy ? ' media-picker__grid--busy' : '')}>
        {shown.map((it, i) => (
          <div key={it.id} className="media-picker__cell">
            <button
              type="button"
              className="media-picker__tile"
              onClick={() => onPick(it)}
              disabled={busy}
              title={it.label || it.id}
            >
              {kind === 'video' ? (
                <span className="media-picker__tile-frame media-picker__tile-frame--video" aria-hidden="true">
                  🎞
                </span>
              ) : (
                <img
                  className="media-picker__tile-frame"
                  src={`/api/bank/file/${encodeURIComponent(it.id)}`}
                  alt=""
                  loading="lazy"
                />
              )}
              <span className="media-picker__tile-label">{it.label || it.id}</span>
              {it.meta?.role && <span className="media-picker__tile-role">{it.meta.role}</span>}
            </button>
            <button
              type="button"
              className="asset-magnify"
              title="Take a closer look"
              aria-label={`Preview ${it.label || it.id}`}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                setMagnified(i);
              }}
            >
              <ZoomIn />
            </button>
          </div>
        ))}
        {shown.length === 0 && (
          <p className="muted small media-picker__empty">Nothing matches{q ? ` "${query.trim()}"` : ''}.</p>
        )}
      </div>

      {magnified != null && shown.length > 0 && (
        <AssetPreviewModal
          items={shown.map((it) => ({
            key: it.id,
            src: `/api/bank/file/${encodeURIComponent(it.id)}`,
            kind: kind === 'video' ? 'video' : 'image',
            label: it.label || it.id,
          }))}
          index={Math.min(magnified, shown.length - 1)}
          onClose={() => setMagnified(null)}
        />
      )}
    </div>
  );
}
